import React, { useState } from 'react';
import { useChallenge } from '../../context/ChallengeContext';
import { ChallengeCard } from './ChallengeCard';
import { Badge } from '../UI/Badge';
import styles from './SideQuests.module.css';

/**
 * SideQuests component displays optional challenges
 * Features:
 * - Consumes ChallengeContext for state management
 * - Renders ChallengeCard for each of 4 side quests
 * - Accordion pattern for collapsible content
 * - Responsive grid layout (1 col mobile, 2 col desktop)
 * - Optional badge indicator
 */
export const SideQuests: React.FC = () => {
  const { sideQuests, toggleChallengeStatus, isLoading } = useChallenge();
  const [expandedQuests, setExpandedQuests] = useState<Set<string>>(new Set());

  const toggleExpanded = (questId: string) => {
    setExpandedQuests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questId)) {
        newSet.delete(questId);
      } else {
        newSet.add(questId);
      }
      return newSet;
    });
  };

  return (
    <section className={styles.sideQuestsSection}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>⚡ Side Quests</h2>
          <Badge variant="default" className={styles.optionalBadge}>
            Optional
          </Badge>
        </div>
        <p className={styles.subtitle}>
          Earn extra recognition by completing these optional challenges
        </p>
      </div>

      <div className={styles.questsGrid}>
        {sideQuests.map((quest) => {
          const isExpanded = expandedQuests.has(quest.id);
          
          return (
            <div key={quest.id} className={styles.questWrapper}>
              <button
                className={styles.accordionHeader}
                onClick={() => toggleExpanded(quest.id)}
                aria-expanded={isExpanded}
              >
                <div className={styles.accordionTitle}>
                  <span className={styles.questTitle}>{quest.title}</span>
                  {quest.isCompleted && (
                    <Badge variant="cyan" className={styles.completedIndicator}>
                      ✓
                    </Badge>
                  )}
                </div>
                <span className={styles.accordionIcon}>
                  {isExpanded ? '−' : '+'}
                </span>
              </button>

              {isExpanded && (
                <div className={styles.accordionContent}>
                  <ChallengeCard
                    challenge={quest}
                    onToggleComplete={toggleChallengeStatus}
                    isLoading={isLoading}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
